<template>
  <div class="container-fluid p-0">
    
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="row justify-content-center g-0">
      <div class="col-md-6 col-lg-5 col-sm-12 p-0">
        <div class="card shadow mt-5 mx-3">
          <div class="card-body p-4">
            <div class="text-center mb-4">
              <img src="/src/assets/img/quitly-blue.svg" alt="Quitly" class="register-logo mb-3" />
              <h2 id="main-content" style="color: var(--bs-primary);">Registrieren</h2>
            </div>

            <form @submit.prevent="handleRegister" novalidate>
              
              <div v-if="errorMessage" class="alert alert-danger" role="alert">
                {{ errorMessage }}
              </div>

              <div class="text-muted small mb-3 text-end">* Pflichtfeld</div>

              
              <div class="mb-3">
                <label for="firstName" class="form-label">Vorname *</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <font-awesome-icon icon="user" />
                  </span>
                  <input
                    v-model="firstName"
                    id="firstName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': firstNameError }"
                    :placeholder="'Vorname'"
                    required
                    @input="firstNameError = ''"
                    tabindex="1"
                  />
                </div>
                <div v-if="firstNameError" class="invalid-feedback d-block">
                  {{ firstNameError }}
                </div>
              </div>

              <div class="mb-3">
                <label for="lastName" class="form-label">Nachname *</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <font-awesome-icon icon="user" />
                  </span>
                  <input
                    v-model="lastName"
                    id="lastName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': lastNameError }"
                    :placeholder="'Nachname'"
                    required
                    @input="lastNameError = ''"
                    tabindex="2"
                  />
                </div>
                <div v-if="lastNameError" class="invalid-feedback d-block">
                  {{ lastNameError }}
                </div>
              </div>

              
              <div class="mb-3">
                <label for="email" class="form-label">E-Mail *</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <font-awesome-icon icon="envelope" />
                  </span>
                  <input
                    v-model="email"
                    id="email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': emailError }"
                    :placeholder="'E-Mail'"
                    required
                    autocomplete="email"
                    @invalid="onInvalidEmail"
                    @input="emailError = ''"
                    tabindex="3"
                  />
                </div>
                <div v-if="emailError" class="invalid-feedback d-block">
                  {{ emailError }}
                </div>
              </div>

              
              <div class="mb-3">
                <label for="password" class="form-label">Passwort *</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <font-awesome-icon icon="lock" />
                  </span>
                  <input
                    v-model="password"
                    id="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': passwordError }"
                    :placeholder="'Passwort'"
                    required
                    minlength="8"
                    autocomplete="new-password"
                    @input="onPasswordChange"
                    tabindex="4"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="togglePasswordVisibility"
                    tabindex="9"
                  >
                    <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
                  </button>
                </div>
                <div v-if="passwordError" class="invalid-feedback d-block">
                  {{ passwordError }}
                </div>
                
                <div v-if="password" class="mt-2">
                  <div class="d-flex justify-content-between">
                    <small>Passwortstärke:</small>
                    <small :class="getTextColorClass(passwordStrength.class)">
                      {{
                        passwordStrength.class === 'weak'
                          ? 'Schwach'
                          : passwordStrength.class === 'medium'
                            ? 'Mittel'
                            : passwordStrength.class === 'good'
                              ? 'Gut'
                              : passwordStrength.class === 'strong'
                                ? 'Sehr stark'
                                : ''
                      }}
                    </small>
                  </div>
                  <div class="progress quitly-progress-medium">
                    <div
                      class="progress-bar quitly-progress-animated"
                      :style="{ width: passwordStrength.percent + '%' }"
                      :class="getProgressBarClass(passwordStrength.class)"
                    ></div>
                  </div>
                  <div class="mt-1">
                    <small class="text-muted">Passwortanforderungen</small>
                  </div>
                </div>
              </div>

              
              <div class="mb-3">
                <label for="confirmPassword" class="form-label">Passwort bestätigen *</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <font-awesome-icon icon="lock" />
                  </span>
                  <input
                    v-model="confirmPassword"
                    id="confirmPassword"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': confirmPasswordError }"
                    :placeholder="'Passwort bestätigen'"
                    required
                    autocomplete="new-password"
                    @input="validateConfirmPassword"
                    tabindex="5"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="toggleConfirmPasswordVisibility"
                    tabindex="10"
                  >
                    <font-awesome-icon :icon="showConfirmPassword ? 'eye-slash' : 'eye'" />
                  </button>
                </div>
                <div v-if="confirmPasswordError" class="invalid-feedback d-block">
                  {{ confirmPasswordError }}
                </div>
              </div>

              
              <div class="mb-4">
                <div class="form-check form-switch form-switch-lg">
                  <input
                    v-model="acceptTerms"
                    id="terms"
                    type="checkbox"
                    class="form-check-input"
                    :class="{ 'is-invalid': termsError }"
                    required
                    @invalid="onInvalidTerms"
                    @change="termsError = ''"
                    tabindex="6"
                    role="switch"
                  />
                  <label for="terms" class="form-check-label ms-3">
                    Ich stimme den
                    <a href="https://quitly.de/datenschutz/agb/" class="terms-link" target="_blank" tabindex="11">Nutzungsbedingungen</a>
                    und 
                    <a href="https://quitly.de/datenschutz/" class="terms-link" target="_blank" tabindex="12">Datenschutzrichtlinien</a>
                    zu
                  </label>
                  <div v-if="termsError" class="invalid-feedback d-block">
                    {{ termsError }}
                  </div>
                </div>
              </div>

              <div class="d-grid gap-2">
                <button type="submit" class="btn btn-primary" :disabled="isLoading" tabindex="7">
                  <span
                    v-if="isLoading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{ isLoading ? 'Registrierung läuft...' : 'Registrieren' }}
                </button>
              </div>
            </form>

            <div class="text-center mt-4">
              <p class="mb-0">
                {{ 'Bereits ein Konto?' }}
                <router-link to="/auth/login" class="text-decoration-none" tabindex="8">
                  {{ 'Anmelden' }}
                </router-link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { register } from '@/api/auth'
import { showError, showSuccess } from '@/services/notification'
import { storeToRefs } from 'pinia'

const authStore = useAuthStore()
const router = useRouter()


const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const acceptTerms = ref(false)
const firstNameError = ref('')
const lastNameError = ref('')
const emailError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')
const termsError = ref('')
const loading = ref(false)
const errorMessage = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const passwordStrength = ref({ percent: 0, class: 'weak', text: '' })


const isValid = computed(() => {
  return (
    firstName.value.trim() !== '' &&
    lastName.value.trim() !== '' &&
    email.value.trim() !== '' &&
    password.value.length >= 8 &&
    password.value === confirmPassword.value &&
    acceptTerms.value &&
    passwordStrength.value.class !== 'weak'
  )
})


const onInvalidEmail = (e) => {
  e.preventDefault()
  if (e.target.validity.valueMissing) {
    emailError.value = 'Diese E-Mail-Adresse ist erforderlich.'
  } else if (e.target.validity.typeMismatch) {
    emailError.value = 'Bitte gib eine gültige E-Mail-Adresse ein.'
  }
}


const onPasswordChange = () => {
  passwordError.value = ''
  const pwd = password.value

  if (!pwd) {
    passwordStrength.value = { percent: 0, class: 'weak', text: '' }
    return
  }

  if (pwd.length < 8) {
    passwordError.value = 'Das Passwort muss mindestens 8 Zeichen lang sein.'
    passwordStrength.value = { class: 'weak', percent: 25 }
    return
  }


  let strength = 0


  if (pwd.length >= 8) strength += 1
  if (pwd.length >= 12) strength += 1


  if (/[A-Z]/.test(pwd)) strength += 1
  if (/[a-z]/.test(pwd)) strength += 1
  if (/[0-9]/.test(pwd)) strength += 1
  if (/[^A-Za-z0-9]/.test(pwd)) strength += 1


  let strengthClass = 'weak'
  let percent = 0

  if (strength <= 2) {
    strengthClass = 'weak'
    percent = 25
  } else if (strength <= 4) {
    strengthClass = 'medium'
    percent = 50
  } else if (strength <= 5) {
    strengthClass = 'good'
    percent = 75
  } else {
    strengthClass = 'strong'
    percent = 100
  }

  passwordStrength.value = {
    percent: percent,
    class: strengthClass,
  }

  validateConfirmPassword()
}


const validateConfirmPassword = () => {
  confirmPasswordError.value = ''

  if (!confirmPassword.value) {
    return
  }

  if (password.value !== confirmPassword.value) {
    confirmPasswordError.value = 'Die Passwörter stimmen nicht überein.'
    return false
  }

  return true
}

const onInvalidTerms = (e) => {
  e.preventDefault()
  if (e.target.validity.valueMissing) {
    termsError.value = 'Du musst die Nutzungsbedingungen akzeptieren.'
  }
}


const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}


const getProgressBarClass = (strengthClass) => {
  switch (strengthClass) {
    case 'weak':
      return 'bg-danger'
    case 'medium':
      return 'bg-warning'
    case 'good':
      return 'bg-info'
    case 'strong':
      return 'bg-success'
    default:
      return 'bg-danger'
  }
}

const getTextColorClass = (strengthClass) => {
  switch (strengthClass) {
    case 'weak':
      return 'text-danger'
    case 'medium':
      return 'text-warning'
    case 'good':
      return 'text-info'
    case 'strong':
      return 'text-success'
    default:
      return 'text-danger'
  }
}


const handleRegister = async (e) => {

  firstNameError.value = ''
  lastNameError.value = ''
  emailError.value = ''
  passwordError.value = ''
  confirmPasswordError.value = ''
  termsError.value = ''
  errorMessage.value = ''


  const form = e.target
  const formIsValid = form.checkValidity() && validateConfirmPassword() && isValid.value

  if (!formIsValid) {

    if (!firstName.value) firstNameError.value = 'Dieses Feld ist erforderlich.'
    if (!lastName.value) lastNameError.value = 'Dieses Feld ist erforderlich.'
    if (!email.value) emailError.value = 'Dieses Feld ist erforderlich.'
    if (!password.value) passwordError.value = 'Dieses Feld ist erforderlich.'
    if (!confirmPassword.value) confirmPasswordError.value = 'Dieses Feld ist erforderlich.'
    if (!acceptTerms.value) termsError.value = 'Du musst die Nutzungsbedingungen akzeptieren.'
    return
  }

  try {
    loading.value = true


    const userData = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      password: password.value,
    }

    const response = await register(userData)


    showSuccess('Du hast dich erfolgreich registriert. Bitte überprüfe deine E-Mail zur Verifizierung. Schau notfalls beim Spam vorbei.', 5000)


    setTimeout(() => {
      router.push('/auth/login')
    }, 1000)
  } catch (error) {
    console.error('Registrierung fehlgeschlagen:', error)
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage.value = error.response.data.message
      showError(error.response.data.message)
    } else {
      errorMessage.value = 'Registrierung fehlgeschlagen.'
      showError('Deine Registrierung ist fehlgeschlagen. Versuche es erneut. Oder melde dich bei uns.', 5000)
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
/* Register Logo Styling */
.register-logo {
  height: 60px;
  width: auto;
}

/* Form Switch Styling */
.form-switch-lg .form-check-input {
  width: 3rem;
  height: 1.5rem;
  border-radius: 1rem;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  transition: all 0.15s ease-in-out;
}

.form-switch-lg .form-check-input:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.form-switch-lg .form-check-input:focus {
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
  border-color: var(--bs-primary);
}

.form-switch-lg .form-check-input:not(:checked) {
  background-color: #e9ecef;
  border-color: #ced4da;
}

.form-switch-lg .form-check-input::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(1.5rem - 4px);
  height: calc(1.5rem - 4px);
  background-color: #fff;
  border-radius: 50%;
  transition: transform 0.15s ease-in-out;
}

.form-switch-lg .form-check-input:checked::before {
  transform: translateX(1.5rem);
}

/* Terms Link Styling */
.terms-link {
  color: var(--bs-primary);
  text-decoration: none;
  font-weight: 500;
}

.terms-link:hover {
  color: var(--bs-primary);
  text-decoration: underline;
}

/* Progress Bar Styling */
.quitly-progress-medium {
  height: 8px;
  border-radius: 4px;
  background-color: #e9ecef;
}

.quitly-progress-animated {
  transition: width 0.3s ease-in-out;
  border-radius: 4px;
}

/* Input Group Styling */
.input-group-text {
  background-color: #f8f9fa;
  border-color: #ced4da;
  color: #6c757d;
}

.form-control:focus + .input-group-text,
.input-group:focus-within .input-group-text {
  border-color: var(--bs-primary);
  background-color: #f8f9fa;
}

/* Button Styling */
.btn-primary {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.btn-primary:hover {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  opacity: 0.9;
}

.btn-primary:focus {
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
}

/* Card Styling */
.card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
}

.card-body {
  padding: 2rem;
}

/* Skip Links */
.skip-links {
  position: absolute;
  top: -40px;
  left: 6px;
  z-index: 1000;
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  z-index: 1000;
  padding: 8px 16px;
  background: var(--bs-primary);
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
}

.skip-link:focus {
  top: 6px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .card-body {
    padding: 1.5rem;
  }
  
  .form-switch-lg .form-check-input {
    width: 2.5rem;
    height: 1.25rem;
  }
  
  .form-switch-lg .form-check-input:checked::before {
    transform: translateX(1.25rem);
  }
}
</style>
